import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, Lock } from "lucide-react";
import api from "@/lib/api";

// Branded, print-ready Suffle shipping label (4"x6"). Barcode drawn with JsBarcode (CDN).
export default function ParcelLabelPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const bc = useRef(null);

  useEffect(() => { api.get(`/marketplace/shipping/parcel/${id}/label-data`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Label not available")); }, [id]);
  useEffect(() => {
    if (!d?.awb) return;
    const draw = () => { try { window.JsBarcode(bc.current, d.awb, { format: "CODE128", width: 2, height: 46, displayValue: false, margin: 0 }); } catch (e) {} };
    if (window.JsBarcode) return draw();
    const s = document.createElement("script"); s.src = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"; s.onload = draw; document.body.appendChild(s);
  }, [d]);

  if (err) return (
    <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="label-error">
      <Lock className="mx-auto mb-3 h-8 w-8 text-amber-500" />
      <p className="text-sm font-medium text-slate-600">{err}</p>
      <a href="/app/parcels" className="mt-4 inline-flex items-center gap-1 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white" data-testid="label-error-parcels-link">Go to My Parcels</a>
    </div>
  );
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const to = d.to || {}, from = d.from || {};
  const fmtDate = d.booked_at ? new Date(d.booked_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

  return (
    <div className="flex flex-col items-center bg-slate-100 py-6 print:bg-white print:py-0" data-testid="parcel-label-page">
      <style>{`@media print{.no-print{display:none}@page{size:4in 6in;margin:0}body{background:#fff}}`}</style>
      <button onClick={() => window.print()} data-testid="label-print-btn" className="no-print mb-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white"><Printer className="h-4 w-4" /> Print label</button>
      <div style={{ width: 384, background: "#fff", border: "2px solid #0b1220", fontFamily: "Helvetica Neue, Arial, sans-serif", color: "#0b1220" }} data-testid="label-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: "2px solid #0b1220" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src="/suffle-logo-new.png?v=2" alt="Suffle Online" style={{ height: 46, width: "auto", objectFit: "contain" }} data-testid="label-suffle-logo" />
            <div style={{ fontSize: 8, textTransform: "uppercase", letterSpacing: 1, color: "#64748b" }}>Online Logistics</div>
          </div>
          <div style={{ border: "2px solid #0b1220", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>PREPAID</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px dashed #94a3b8" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{d.courier || "Courier"}</div>
          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1, color: "#64748b" }}>{d.eta_days ? `${d.eta_days} day${d.eta_days > 1 ? "s" : ""}` : "Std"}</div>
        </div>
        <div style={{ textAlign: "center", padding: "10px 0 4px", borderBottom: "2px solid #0b1220" }}><svg ref={bc} /><div style={{ fontSize: 15, fontWeight: 800, letterSpacing: 3, marginTop: 2 }}>{d.awb || "—"}</div></div>
        <div style={{ padding: "9px 12px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.5, color: "#64748b", marginBottom: 3 }}>Ship to</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{to.name}</div>
          <div style={{ fontSize: 12, lineHeight: 1.35, marginTop: 2 }}>{to.address}<br />{to.city}, {to.state}</div>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>{to.pincode}</div>
          <div style={{ fontSize: 11, marginTop: 2 }}>Ph: {to.phone}</div>
        </div>
        <div style={{ padding: "9px 12px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.5, color: "#64748b", marginBottom: 3 }}>From (Return)</div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{from.name}</div>
          <div style={{ fontSize: 10, color: "#334155", lineHeight: 1.3 }}>{from.address}, {from.city}, {from.state} — {from.pincode} • {from.phone}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          {[["Order", d.order_id || "—"], ["Date", fmtDate], ["Weight", `${d.weight} kg`], ["Dimensions", d.dims]].map(([k, v], i) => (
            <div key={i} style={{ padding: "7px 12px", borderTop: "1px solid #e2e8f0", borderRight: i % 2 === 0 ? "1px solid #e2e8f0" : "none" }}>
              <div style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.5, color: "#64748b" }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
          <div style={{ gridColumn: "1/3", padding: "7px 12px", borderTop: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.5, color: "#64748b" }}>Contents</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{d.item} (₹{d.declared_value})</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 8, background: "#0b1220", color: "#fff", fontWeight: 800, letterSpacing: 1 }}>
          <span>{from.pincode}</span><span style={{ color: "#818cf8" }}>➜</span><span>{to.pincode}</span>
        </div>
        <div style={{ padding: "7px 12px", fontSize: 8, color: "#64748b", textAlign: "center", letterSpacing: 0.5 }}>Booked via Suffle Online • Prepaid, do not collect cash • suffleonline.com</div>
      </div>
    </div>
  );
}
